import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../common/Modal';
import RecipientPicker from './RecipientPicker';
import { useAddMembers } from '../../hooks/chat/useChatRoomActions';
import type { TenantMember } from '../../types/chat';

interface AddMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  excludeUserIds?: string[];
}

const AddMembersModal: React.FC<AddMembersModalProps> = ({
  isOpen,
  onClose,
  roomId,
  excludeUserIds = [],
}) => {
  const [selected, setSelected] = useState<TenantMember[]>([]);
  const addMembers = useAddMembers(roomId);

  const handleAdd = async () => {
    const fresh = selected.filter((m) => !excludeUserIds.includes(m.user_id));
    if (fresh.length === 0) {
      toast.error('Pick at least one new member.');
      return;
    }
    try {
      await addMembers.mutateAsync(fresh.map((m) => m.user_id));
      toast.success(`Added ${fresh.length} member${fresh.length > 1 ? 's' : ''}`);
      setSelected([]);
      onClose();
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Failed to add members');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add members"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-medium text-text-secondary hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={selected.length === 0 || addMembers.isPending}
            className="px-4 py-2 rounded-full text-sm font-semibold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {addMembers.isPending ? 'Adding…' : 'Add to room'}
          </button>
        </div>
      }
    >
      <div className="py-2">
        <RecipientPicker
          selected={selected}
          onChange={setSelected}
          multi
          autoFocus
          placeholder="Search teammates…"
        />
        <p className="mt-3 text-xs text-text-tertiary">
          Selected members will see the conversation and any new messages.
        </p>
      </div>
    </Modal>
  );
};

export default AddMembersModal;
